import type { FolderPathSegment } from "@/domains/documents/utils/folderPathResolver";

/**
 * The three statuses every project has. They cannot be renamed, recoloured,
 * reordered, hidden or deleted, so an issue always has a status that resolves.
 */
export const BUILT_IN_ISSUE_STATUSES = ["in_progress", "resolved", "closed"] as const;
export type BuiltInIssueStatus = (typeof BUILT_IN_ISSUE_STATUSES)[number];

/**
 * A status KEY: one of the built-ins above, or the `key` of a
 * `pm_issue_statuses` row defined for the issue's building project.
 *
 * Deliberately open. Never index a `Record<IssueStatus, T>` with one — resolve
 * it through `resolveStatus()` in `domains/pm/statuses/statusCatalogue`, which
 * always returns a descriptor, including for a key whose row has been deleted.
 */
export type IssueStatus = string;

/**
 * What a status MEANS, as opposed to what it looks like. "open" is live work:
 * it can go overdue, it counts in My Tasks and in a project's open-issue count.
 * Mirrors pm_issue_statuses.category.
 */
export type IssueStatusCategory = "open" | "done";

/** A user-defined status, scoped to one building project. */
export interface PMIssueStatus {
  id: number;
  building_project_id: number;
  key: string;
  label_en: string;
  label_et: string | null;
  category: IssueStatusCategory;
  dot_color: string;
  bg_color: string;
  text_color: string;
  sort_order: number;
}

/** How a successor is scheduled against its predecessor. */
export type IssueDependencyType = "FS" | "SS" | "FF" | "SF";

/**
 * `hard` drags the successor when the predecessor moves. `soft` never moves
 * anything — it only warns when the plan stops adding up.
 */
export type IssueDependencyHardness = "hard" | "soft";

export interface PMIssueDependency {
  id: number;
  predecessor_issue_id: number;
  successor_issue_id: number;
  dep_type: IssueDependencyType;
  /** Days added to the constraint. Negative is a lead (overlap). */
  lag_days: number;
  hardness: IssueDependencyHardness;
}

/** One issue the cascade would move, or did. */
export interface ScheduleMove {
  issue_id: number;
  issue_number: number;
  title: string;
  old_start: string | null;
  old_due: string | null;
  new_start: string | null;
  new_due: string | null;
}

/** Something the cascade could not honour: a pinned issue, or a soft edge. */
export interface ScheduleWarning {
  kind: "pinned" | "soft_violation";
  issue_id: number;
  issue_number: number;
  title: string;
  required_start: string | null;
  current_start: string | null;
  predecessor_issue_id?: number;
  predecessor_issue_number?: number;
  dep_type?: IssueDependencyType;
}

/** The shape every schedule preview and apply RPC returns. */
export interface SchedulePreview {
  /** The single edited issue, on the one-issue path. Null for a batch. */
  target: ScheduleMove | null;
  /** Every issue the user placed. One entry on the one-issue path. */
  targets?: ScheduleMove[];
  moved: ScheduleMove[];
  warnings: ScheduleWarning[];
  completion: { old: string | null; new: string | null; delta_days: number | null };
  /** Only on an apply result. */
  cascaded?: boolean;
}

/** One issue's complete new date pair, as the batch RPCs take them. */
export interface ScheduleDateChange {
  issue_id: number;
  start_date: string | null;
  due_date: string | null;
}

/**
 * Dependency changes that have not been written. The preview overlays these on
 * the real graph so a staged link affects the cascade before it exists.
 */
export interface ScheduleEdgeChanges {
  add?: Array<{
    predecessor_issue_id: number;
    successor_issue_id: number;
    dep_type?: IssueDependencyType;
    lag_days?: number;
    hardness?: IssueDependencyHardness;
  }>;
  update?: Array<
    { id: number } & Partial<Pick<PMIssueDependency, "dep_type" | "lag_days" | "hardness">>
  >;
  remove?: number[];
}

/** A saved capture of one building project's schedule. */
export interface ScheduleSnapshot {
  id: number;
  building_project_id: number;
  label: string | null;
  schema_version: number;
  issue_count: number;
  dependency_count: number;
  completion_date: string | null;
  created_at: string;
  created_by: number;
  /** Only fetched when a snapshot is opened — the list omits it. */
  payload?: ScheduleSnapshotPayload;
}

export interface ScheduleSnapshotPayload {
  captured_at: string;
  issues: SnapshotIssue[];
  dependencies: SnapshotDependency[];
}

/**
 * An issue as it was. Assignee and discipline NAMES are carried, not just ids,
 * so a snapshot still reads correctly after someone leaves the organisation or
 * a discipline is renamed.
 */
export interface SnapshotIssue {
  id: number;
  issue_number: number;
  title: string;
  status: IssueStatus;
  start_date: string | null;
  due_date: string | null;
  schedule_pinned: boolean;
  parent_issue_id: number | null;
  assignees: Array<{ id: number; name: string }>;
  disciplines: Array<{ id: number; name: string }>;
}

export interface SnapshotDependency {
  predecessor_issue_id: number;
  successor_issue_id: number;
  dep_type: IssueDependencyType;
  lag_days: number;
  hardness: IssueDependencyHardness;
}

export interface PMIssue {
  id: number;
  building_id: number;
  org_id: string;
  issue_number: number;
  title: string;
  status: IssueStatus;
  /** Scheduled start. NULL = unscheduled. Deliberately NOT constrained
   *  against due_date in the DB — see pipeline migration 20260911101416,
   *  which MUST be deployed before this UI: PostgREST rejects an entire
   *  PATCH/POST naming an unknown column, so an early UI would break every
   *  issue write, not just the date field. */
  start_date: string | null;
  due_date: string | null;
  building_project_id: number;
  protocol_row_id: number | null;
  parent_issue_id: number | null;
  created_at: string;
  created_by: number;
  modified_at: string | null;
  modified_by: number | null;
  deleted_at: string | null;
  // Joined fields
  disciplines?: Discipline[];
  assignees?: UserProfile[];
  comments_count?: number;
  /** Checklist progress. `undefined` = not hydrated (or the table is not
   *  deployed yet); 0/0 = no checklist. */
  checklist_total?: number;
  checklist_done?: number;
  // Unread @-mentions on this issue for the current user (WHA-3339).
  unread_mention_count?: number;
  linked_documents?: LinkedDocument[];
  // BIM elements linked to this issue (WHA-3521).
  linked_elements?: LinkedElement[];
  /** Saved 3D view. `undefined` = not hydrated; `null` = hydrated, none saved. */
  viewpoint?: IssueViewpoint | null;
  /** True once a user set these dates by hand. The cascade never moves a
   *  pinned issue; it reports it as a warning instead. */
  schedule_pinned?: boolean;
  /** Set when this issue is the parent every comment thread on a document
   *  hangs under (`document_issue_roots`). `null` = hydrated, not a root;
   *  `undefined` = not hydrated. */
  document_root?: DocumentIssueRoot | null;
  /** The stored title, kept beside the display title so a save that did not
   *  touch the title can tell. */
  title_raw?: string;
}

/** The `document_issue_roots` row behind a document's parent issue. */
export interface DocumentIssueRoot {
  filename: string;
  /** While true the app renders "Comments for {filename}" in the viewer's
   *  language; a rename by a person clears it in the database. */
  auto_title: boolean;
}

/** An exact camera placement. Mirrors the viewer's `IfcCameraState`; declared
 * here so the PM domain does not import from the 3D viewer. */
export interface IssueViewpointCamera {
  position: [number, number, number];
  target: [number, number, number];
}

/**
 * A saved 3D view attached to an issue — what the reporter was looking at, not
 * just which element they meant.
 *
 * COORDINATES ARE ABSOLUTE (pre-recentre). The viewer re-centres every model on
 * the first-loaded one's bbox centre, so raw world X/Z depends on load order and
 * is not reproducible. Conversion both ways lives in
 * `domains/buildings/components/ifc-viewer/issueViewpoint.ts`; nothing else
 * should add or subtract the offset.
 */
export interface IssueViewpoint {
  /** Payload version of `camera` / `section`. 1 = the shape below. */
  schema_version: number;
  camera: IssueViewpointCamera;
  /** null = camera-only viewpoint (no cut was active when it was saved). */
  section: IssueViewpointSection | null;
  element_guid: string | null;
  anchor_id: number | null;
  /** Models VISIBLE when it was saved, in load order. A model switched off with
   * its eye icon is deliberately left out rather than recorded separately: the
   * table has no column for a hidden set, and "don't load it" reproduces what
   * the reporter saw exactly as well as "load it, then hide it". Best-effort —
   * a document deleted since simply cannot be reloaded. */
  model_document_ids: number[];
  /** The document whose bbox established the recentre offset. Not needed for
   * correctness (restore subtracts the CURRENT offset) but loading it first
   * reproduces the original scene exactly. */
  scene_anchor_document_id: number | null;
}

/** The cut, in absolute coordinates. Structurally the viewer's `SectionState`,
 * restated so the PM types stay free of a 3D-viewer import. */
export interface IssueViewpointSection {
  box: { x: [number, number]; y: [number, number]; z: [number, number] };
  free: {
    id: string;
    normal: [number, number, number];
    point: [number, number, number];
    label: string;
  }[];
  suspended: boolean;
}

/** One persisted 2D location for an issue created from a native DWG. X/Y are
 * absolute drawing coordinates, never screen pixels, so pan and zoom cannot
 * detach the issue bubble from its source location. */
export interface DrawingIssueAnchor {
  issue_id: number;
  issue_number: number;
  title: string;
  building_id: number;
  status: IssueStatus;
  document_id: number;
  schema_version: 1;
  x: number;
  y: number;
}

export interface LinkedDocument {
  id: number;
  filename: string;
  state: string;
  content_type: string | null;
  file_path: string;
  size: number | null;
  created_at: string;
  parent_document_id: number | null;
  version: number | null;
  // WHA-3457: project / folder context, used to resolve the breadcrumb tooltip.
  project_id: number | null;
  folder_id: number | null;
  /** In the chatbot's hidden attachments project — never rendered as a path. */
  hidden?: boolean | null;
  /** A chat upload: it parks once readable and is never fully processed. */
  chatbot_attachment?: boolean | null;
  /** WHA-3457: interactive folder-path segments for the hover tooltip,
   *  resolved by the repository. Null when the document has no
   *  project/folder context. */
  breadcrumbSegments?: FolderPathSegment[] | null;
}

/** A BIM element linked to a PM issue (WHA-3521). Identified by its stable
 * anchor (data.ifc_element_anchors.id) so the link survives IFC re-upload;
 * guid/name/type are denormalised for display + "View in 3D" navigation. */
export interface LinkedElement {
  anchor_id: number;
  element_guid: string;
  element_name: string | null;
  element_type: string;
  /** IFC document this link was authored against — drives the "open in 3D"
   * deep-link (?ifc=<doc>&focus=<guid>). Null once the source doc is
   * hard-deleted; the UI then falls back to the building's newest IFC. */
  ifc_document_id: number | null;
}

export interface PMIssueComment {
  id: number;
  issue_id: number;
  author_id: number;
  body: string;
  created_at: string;
  modified_at: string | null;
  modified_by: number | null;
  author?: UserProfile;
  // Documents linked to this comment via pm_issue_comment_documents
  // (WHA-3376). Hydrated by listComments alongside the author profile.
  linked_documents?: LinkedDocument[];
}

/** WHA-3732 / WHA-3733: a single entry in an issue's change log. Rows are
 * written by DB triggers (see pm_issue_activity migrations). old_value /
 * new_value / metadata are jsonb whose shape depends on action_type — see
 * renderActivityLine for the resolution rules. */
export type PMActivityActionType =
  | "issue_created"
  | "issue_deleted"
  | "issue_restored"
  | "title_changed"
  | "status_changed"
  | "start_date_changed"
  | "due_date_changed"
  | "parent_changed"
  | "assignee_added"
  | "assignee_removed"
  | "discipline_added"
  | "discipline_removed"
  | "document_attached"
  | "document_detached"
  | "element_linked"
  | "element_unlinked"
  | "viewpoint_saved"
  | "viewpoint_cleared"
  | "dependency_added"
  | "dependency_removed"
  | "schedule_pin_changed"
  | "drawing_anchor_saved"
  | "drawing_anchor_cleared"
  | "comment_added"
  | "comment_edited"
  | "checklist_item_added"
  | "checklist_item_removed"
  | "checklist_item_renamed"
  | "checklist_item_checked"
  | "checklist_item_unchecked";

/** One line of an issue's checklist (`pm_issue_checklist_items`). `done_at` /
 *  `done_by` are stamped by the server when `is_done` flips; never send them. */
export interface PMIssueChecklistItem {
  id: number;
  issue_id: number;
  text: string;
  position: number;
  is_done: boolean;
  done_at: string | null;
  done_by: number | null;
}

export interface PMIssueActivity {
  id: number;
  issue_id: number;
  building_id: number;
  org_id: string;
  building_project_id: number;
  action_type: PMActivityActionType;
  /** NULL = system / service-role writer (renders as "System"). */
  actor_id: number | null;
  field_name: string | null;
  old_value: unknown;
  new_value: unknown;
  /** Display labels are snapshotted in metadata by the DB triggers:
   *  actor_name, user_name (assignee), discipline, filename, comment_id. */
  metadata: Record<string, unknown>;
  created_at: string;
  /** Hydrated client-side from user_profiles for the row's avatar (current
   *  picture/colour); null when actor_id is null (system). The line *text*
   *  still uses the snapshotted metadata.actor_name. */
  actor?: UserProfile | null;
  /** Feed-only (WHA-3733): the parent issue's number + title for the row ref,
   *  hydrated by getActivityFeedForBuilding. */
  issue_number?: number;
  issue_title?: string;
}

export interface Discipline {
  id: number;
  /** Estonian name (design_disciplines.name). */
  name: string;
  /** English name (design_disciplines.name_en) — may be null. */
  name_en: string | null;
  /** Estonian code (design_disciplines.abbreviation). */
  abbreviation: string;
  /** English code (design_disciplines.abbreviation_en) — may be null. */
  abbreviation_en: string | null;
  description: string | null;
  bg_color: string | null;
  text_color: string | null;
  group_code: string | null;
  /** English group code (design_disciplines.group_code_en) — may be null. */
  group_code_en: string | null;
}

export interface UserProfile {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  avatar_url?: string | null;
  color?: string | null;
}

export interface PMIssueFilters {
  status?: IssueStatus[];
  disciplineIds?: number[];
  assigneeUserIds?: number[];
  buildingProjectId?: number;
  search?: string;
  // WHA-3423: true = only issues with ≥1 linked document, false = only
  // issues with none. Undefined leaves linked documents unfiltered.
  hasLinkedDocuments?: boolean;
  // WHA-3423: true = only issues with ≥1 comment, false = only issues
  // with none. Undefined leaves comments unfiltered.
  hasComments?: boolean;
  // WHA-3644 (My Issues): issues created by any of these user ids.
  createdByUserIds?: number[];
  // WHA-3644 (My Issues): issues where this user is @mentioned in any
  // comment (read or unread).
  mentionedUserId?: number;
  // My Issues / Overdue tab: only issues whose due_date is strictly before
  // this local YYYY-MM-DD date (use overdueDueBefore()). Combined with
  // status: ["in_progress"] this is the server-side form of isIssueOverdue.
  dueBefore?: string;
}

export interface ProtocolData {
  issues: PMIssue[];
  activity: PMIssueActivity[];
  /**
   * The comments the export prints, keyed by issue.
   *
   * `undefined` means "never asked for", which is NOT `new Map()` ("asked
   * for, there are none") — the document must not print an empty comments
   * section for a period whose comments were never fetched. `getProtocolData`
   * never sets this; the wizard fetches it separately when the column is on.
   */
  comments?: Map<number, PMIssueComment[]>;
  /** The comment scan hit its cap, so the printed comments are a slice. */
  commentsTruncated?: boolean;
  /**
   * The issues that slice cut short. Their `comments` entry is partial or
   * missing altogether, so the table prints no count for them: "0" would be a
   * wrong number stated as a fact, where a dash says the export cannot tell.
   */
  commentsIncomplete?: ReadonlySet<number>;
  /**
   * The period held more activity than one export pulls, so both lists are the
   * newest slice of it rather than the whole period. The document prints this
   * as a caveat instead of reading as a complete record.
   */
  truncated?: boolean;
  /**
   * Some activity in the period belongs to an issue that could not be read
   * back, so it is missing from the lists entirely. Printed as its own caveat:
   * a document short of changes it never mentions is a false record.
   */
  omitted?: boolean;
}
